<template>
  <!--
    MK3 topbar — white shadow-sm header with hamburger + breadcrumb on
    the left, notifications + user menu on the right. Sourced from
    /Users/simoncrunden/travel-analytics's layouts/TopBar.vue (MK3 visual
    reference) and adapted to:
      - Tailwind `tw-` prefix
      - FontAwesome icons (decision: keep FA across cutover)
      - MK2's user shape (`useAccountsStore` exposes `user.name` /
        `user.email`, not MK3's `username`)

    Notifications are intentionally a stub for Phase 1a — the MK3
    reference uses hardcoded sample data too. Hooked up properly in
    a later phase when the backend endpoint exists.
  -->
  <header class="tw-bg-white tw-shadow-sm">
    <div class="tw-flex tw-items-center tw-justify-between tw-h-16 tw-px-6">
      <!--
        Left: hamburger (mobile only) + filters strip. The sidebar
        is normally toggled via its own internal chevron / avatar
        controls; the hamburger is kept only for small screens
        (`<lg`) where the sidebar overlays and users need a topbar
        affordance to open it.

        The named router-view `header` mounts ViewHeader.vue (and any
        future per-route filter container) inline here, replacing the
        dedicated strip the AppShell used to render below the topbar.
      -->
      <div class="tw-flex tw-items-center tw-gap-4 tw-min-w-0 tw-flex-1">
        <button
          type="button"
          class="tw-p-2 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors tw-text-gray-600 tw-border-0 tw-bg-transparent tw-cursor-pointer lg:tw-hidden"
          aria-label="Toggle sidebar"
          @click="uiStore.toggleSidebar"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="2"
            stroke="currentColor"
            class="tw-w-6 tw-h-6"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
            />
          </svg>
        </button>

        <router-view name="header" />
      </div>

      <!-- Right: notifications + user menu -->
      <div class="tw-flex tw-items-center tw-gap-4">
        <!-- Notifications (stub). Wrapper pinned to tw-h-16 (= topbar
             height) so the dropdown's `top-full` lands exactly at the
             topbar's bottom edge. tw-self-stretch alone wasn't enough
             because the right-group parent is content-height, not
             full topbar height — the wrapper would only stretch to
             the right group's height.

             HIDDEN for the initial release (May-2026) — the bell has
             no backing notifications source yet. Flip
             NOTIFICATIONS_ENABLED to true when the backend feed
             (likely sourcing from importer Batch lifecycle +
             budgets.Notification breaches) lands. Keeping the markup
             + handlers in tree so the re-enable is a one-line change. -->
        <div v-if="NOTIFICATIONS_ENABLED" class="tw-relative tw-h-16 tw-flex tw-items-center">
          <button
            type="button"
            class="tw-p-2 tw-relative tw-border-0 tw-bg-transparent tw-cursor-pointer"
            aria-label="Notifications"
            @click.stop="toggleNotifications"
          >
            <i class="fa fa-bell tw-text-lg tw-text-gray-600" />
            <span
              v-if="notificationCount > 0"
              class="tw-absolute tw-top-1 tw-right-1 tw-w-2 tw-h-2 tw-bg-red-500 tw-rounded-full"
            />
          </button>

          <div
            v-if="showNotifications"
            class="tw-absolute tw-right-0 tw-top-full tw-mt-0.5 tw-w-80 tw-bg-white tw-rounded-lg tw-shadow-xl tw-z-50"
          >
            <div class="tw-p-4 tw-border-0 tw-border-b tw-border-solid tw-border-gray-200">
              <h3 class="tw-text-sm tw-font-semibold tw-text-gray-900">
                Notifications
              </h3>
            </div>
            <div class="tw-max-h-96 tw-overflow-y-auto">
              <div
                v-if="notifications.length === 0"
                class="tw-p-4 tw-text-center tw-text-gray-500 tw-text-sm"
              >
                No new notifications
              </div>
              <div
                v-for="n in notifications"
                :key="n.id"
                class="tw-p-4 hover:tw-bg-gray-50 tw-border-0 tw-border-b tw-border-solid tw-border-gray-100 tw-cursor-pointer"
              >
                <p class="tw-text-sm tw-text-gray-900">
                  {{ n.message }}
                </p>
                <p class="tw-text-xs tw-text-gray-500 tw-mt-1">
                  {{ n.time }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- User menu — same h-16 trick as notifications so the
             dropdown lands consistently at topbar bottom + 2px. -->
        <div class="tw-relative tw-h-16 tw-flex tw-items-center">
          <button
            type="button"
            class="tw-flex tw-items-center tw-gap-2 tw-p-2 tw-border-0 tw-bg-transparent tw-cursor-pointer"
            aria-label="User menu"
            @click.stop="toggleUserMenu"
          >
            <div class="tw-w-8 tw-h-8 tw-rounded-full tw-bg-blue-600 tw-flex tw-items-center tw-justify-center">
              <span class="tw-text-sm tw-font-medium tw-text-white">{{ userInitials }}</span>
            </div>
            <span class="tw-hidden md:tw-block tw-text-sm tw-font-medium tw-text-gray-700">
              {{ userDisplayName }}
            </span>
            <i class="fa fa-chevron-down tw-text-gray-600" />
          </button>

          <div
            v-if="showUserMenu"
            class="tw-absolute tw-right-0 tw-top-full tw-mt-0.5 tw-w-56 tw-bg-white tw-rounded-lg tw-shadow-xl tw-z-50"
          >
            <div class="tw-p-4">
              <p class="tw-text-sm tw-font-medium tw-text-gray-900 tw-mb-2">
                {{ userDisplayName }}
              </p>
              <p v-if="user?.email" class="tw-text-xs tw-text-gray-500 tw-mb-2">
                {{ user.email }}
              </p>
            </div>
            <!-- Explicit 1px divider — Tailwind border utilities are
                 unreliable here (preflight off + Bootstrap/CoreUI
                 reset interactions). A 1px-tall coloured div is
                 always-on regardless of class ordering or inherited
                 widths. -->
            <div class="tw-h-px tw-bg-gray-200" />
            <div class="tw-py-2">
              <!-- Switch TMC — only shown to users with more than one
                   accessible client. Single-client users would see a
                   no-op link; staff with multi-tenant access (whose
                   accessible_clients() returns all Clients) see the
                   full picker. Same destination as the MK2 UserBadge
                   menu — /client-selection — which uses
                   accountsStore.selectClient() under the hood. -->
              <router-link
                v-if="hasMultipleClients"
                to="/client-selection"
                class="tw-w-full tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 tw-no-underline"
                @click="showUserMenu = false"
              >
                <i class="fa fa-exchange tw-text-base tw-mr-3" />
                Switch TMC
              </router-link>
              <a
                :href="logoutHref"
                class="tw-w-full tw-flex tw-items-center tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-gray-100"
              >
                <i class="fa fa-sign-out tw-text-base tw-mr-3" />
                Logout
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue'
  import { storeToRefs } from 'pinia'

  import { useAccountsStore } from '@/stores/accounts'
  import { useUiStore } from '@/stores/ui'

  defineOptions({ name: 'Mk3TopBar' })

  const uiStore = useUiStore()
  const { user } = storeToRefs(useAccountsStore())

  // Match the existing MK2 logout flow — UserBadge.vue sends users to
  // `/logout`, which main.ts mounts directly. Keep the same target here
  // so behaviour matches across shells.
  const logoutHref = '/logout'

  // Notifications (stub — same as MK3 reference). The bell is
  // hidden behind NOTIFICATIONS_ENABLED until the backend feed
  // is built; the wiring stays in place so the re-enable is a
  // one-line flip (see template comment for context).
  const NOTIFICATIONS_ENABLED = false
  const showNotifications = ref(false)
  const showUserMenu = ref(false)
  const notifications = ref<Array<{ id: number; message: string; time: string }>>([])

  const notificationCount = computed(() => notifications.value.length)

  const userDisplayName = computed(() => {
    return (user.value as any)?.name || (user.value as any)?.email || 'Guest'
  })

  const userInitials = computed(() => {
    const name = userDisplayName.value
    if (!name || name === 'Guest') return 'G'
    return name.substring(0, 2).toUpperCase()
  })

  // Gate the "Switch TMC" menu item — only useful when the user has
  // more than one accessible client. `user.clients` is populated from
  // the backend's UserSerializer.get_clients, which returns
  // accessible_clients() — i.e. every Client id for staff (Platform
  // Admin), the explicit M2M for everyone else. So this check
  // correctly hides the link for single-tenant non-staff users
  // without false-negatives for staff.
  const hasMultipleClients = computed(() => {
    const clients = (user.value as any)?.clients
    return Array.isArray(clients) && clients.length > 1
  })

  function toggleNotifications () {
    showNotifications.value = !showNotifications.value
    if (showNotifications.value) showUserMenu.value = false
  }
  function toggleUserMenu () {
    showUserMenu.value = !showUserMenu.value
    if (showUserMenu.value) showNotifications.value = false
  }

  function handleClickOutside (e: MouseEvent) {
    const target = e.target as HTMLElement | null
    if (!target) return
    if (!target.closest('[aria-label="Notifications"]') && !target.closest('.tw-w-80')) {
      showNotifications.value = false
    }
    if (!target.closest('[aria-label="User menu"]') && !target.closest('.tw-w-56')) {
      showUserMenu.value = false
    }
  }

  onMounted(() => document.addEventListener('click', handleClickOutside))
  onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
